import React from 'react';
import { Icon } from './Icon';
import { STATUS_META, InspectionStatus } from './StatusChip';

export interface StatusSegmentOption {
  /** Value reported through onChange. */
  id: string;
  /** Drives the icon + color from STATUS_META. */
  status: InspectionStatus;
  /** Override label (defaults to the status label). */
  label?: string;
}

export interface StatusSegmentProps {
  /** Currently selected option id. */
  value?: string;
  /** Called with the chosen option id. */
  onChange?: (id: string) => void;
  /** Defaults to the 4 canonical statuses: Bez uwag / Zalecenie / Brak dostępu / Odmowa. */
  options?: StatusSegmentOption[];
  style?: React.CSSProperties;
}

/**
 * StatusSegment — the 4 big status choices at the top of a Lokal /
 * Inwentaryzacja card. Each segment is ≥72px, icon + label, selectable.
 * Selected segment fills with its status tint + a 2px colored border.
 */
const DEFAULT_OPTIONS: StatusSegmentOption[] = [
  { id: 'ok', status: 'ok' },
  { id: 'rec', status: 'rec' },
  { id: 'noaccess', status: 'noaccess' },
  { id: 'deny', status: 'deny' },
];

export function StatusSegment({ value = 'ok', onChange, options = DEFAULT_OPTIONS, style }: StatusSegmentProps) {
  return (
    <div
      role="radiogroup"
      style={{ display: 'flex', gap: 'var(--space-3)', width: '100%', ...style }}
    >
      {options.map((opt) => {
        const meta = STATUS_META[opt.status] || STATUS_META.todo;
        const selected = value === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange && onChange(opt.id)}
            style={{
              flex: '1 1 0',
              minHeight: 'var(--segment-min)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--space-2)',
              padding: 'var(--space-3) var(--space-2)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-label)',
              fontWeight: selected ? 'var(--weight-bold)' : 'var(--weight-medium)',
              lineHeight: 1.1,
              textAlign: 'center',
              color: selected ? meta.color : 'var(--text-primary)',
              background: selected ? meta.bg : 'var(--surface-card)',
              border: `var(--border-width-strong) solid ${selected ? meta.bar : 'var(--border-strong)'}`,
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
              transition: 'background-color .12s ease, border-color .12s ease',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <Icon name={meta.icon} size={28} strokeWidth={selected ? 2.5 : 2} color={selected ? meta.color : 'var(--text-secondary)'} />
            <span>{opt.label || meta.label}</span>
          </button>
        );
      })}
    </div>
  );
}
